/**
 * Shared hook for managing table filter state.
 *
 * This hook contains filter logic for tables, handling:
 * - Column-based multi-select filters (e.g., classification, creator)
 * - Date range filtering (from/to dates)
 * - Filter change, clear, and reset operations
 * - Active filter count calculation
 * - Date bounds calculation from available date options
 *
 * Used by: VariantCheck (variant search), ProbandsSamplesPage (sample search)
 *
 * @example
 * const {
 *   columnFilters,
 *   dateFrom,
 *   dateTo,
 *   handleFilterChange,
 *   handleFilterClear,
 *   handleClearAllFilters,
 *   getActiveFiltersCount,
 *   getDateBounds,
 * } = useTableFilters({
 *   initialFilters: { classification: [], creator: [] },
 *   dateFilterKey: 'created_date',
 * });
 */
import { useCallback, useState } from 'react';

// Interface for filter state: object with string keys and string array values
// Example: { classification: ['Pathogenic', 'Benign'], creator: ['john'] }
export interface FilterState {
  [key: string]: string[];
}

// Configuration options for the hook
export interface UseTableFiltersOptions {
  initialFilters?: FilterState; // Starting filter state (default: empty object)
  dateFilterKey?: string; // Key name for the date filter column (default: 'date')
}

// Return type of the hook containing all the state and functions the component receives
export interface UseTableFiltersReturn {
  columnFilters: FilterState; // Current filter selections
  dateFrom: string; // Current "from" date value
  dateTo: string; // Current "to" date value
  setDateFrom: (value: string) => void; // Function to set "from" date
  setDateTo: (value: string) => void; // Function to set "to" date
  handleFilterChange: (key: string, values: string[]) => void; // Update a filter's values
  handleFilterClear: (key: string) => void; // Clear a specific filter
  handleClearAllFilters: () => void; // Reset all filters to initial state
  getActiveFiltersCount: () => number; // Calculate total active filters
  getDateBounds: (dates: string[] | undefined) => {
    min: string | undefined;
    max: string | undefined;
  }; // Calculate min/max dates from available options
}

export const useTableFilters = ({
  initialFilters = {},
  dateFilterKey = 'date',
}: UseTableFiltersOptions = {}): UseTableFiltersReturn => {
  // State for column-based filters (e.g., classification, creator)
  const [columnFilters, setColumnFilters] =
    useState<FilterState>(initialFilters);

  // State for date range filtering
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Update a specific filter's values
  // Example: handleFilterChange('classification', ['Pathogenic'])
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setColumnFilters((prev) => ({ ...prev, [key]: values }));
  }, []);

  // Clear a specific filter
  // If clearing the date filter, also reset date range inputs
  const handleFilterClear = useCallback(
    (key: string) => {
      if (key === dateFilterKey) {
        setDateFrom('');
        setDateTo('');
      }
      setColumnFilters((prev) => ({ ...prev, [key]: [] }));
    },
    [dateFilterKey],
  );

  // Reset all filters to their initial state
  const handleClearAllFilters = useCallback(() => {
    setColumnFilters(initialFilters);
    setDateFrom('');
    setDateTo('');
  }, [initialFilters]);

  // Calculate total number of active filters
  // Counts non-empty column filters + date range (if set)
  const getActiveFiltersCount = useCallback(() => {
    return (
      Object.values(columnFilters).filter((values) => values.length > 0)
        .length + (dateFrom || dateTo ? 1 : 0)
    );
  }, [columnFilters, dateFrom, dateTo]);

  // Calculate min and max dates from available date options
  // Used to set min/max attributes on date input fields
  const getDateBounds = useCallback((dates: string[] | undefined) => {
    if (!dates || dates.length === 0) {
      return { min: undefined, max: undefined };
    }
    const sorted = [...dates].sort();
    return { min: sorted[0], max: sorted[sorted.length - 1] };
  }, []);

  // Return all state and functions for the component to use
  return {
    columnFilters,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    handleFilterChange,
    handleFilterClear,
    handleClearAllFilters,
    getActiveFiltersCount,
    getDateBounds,
  };
};
